"use client";

import { type EmbedSearch, SESSION_HEADER } from "@repo/embed";
import {
	useCallback,
	useEffect,
	useEffectEvent,
	useMemo,
	useRef,
	useState,
} from "react";
import type { ShopperPageData } from "~/server/shopper";
import { debugError, debugLog, debugRequest, debugWarn } from "./debug";
import { readSession, type StoredSession, writeSession } from "./session";

/**
 * - starting: reading the tab's session or exchanging a launch token
 * - ready: chatting (anonymously until the first turn opens a session)
 * - launch_required: the retailer only allows chats opened from its app, and there is none
 */
export type SessionStatus = "starting" | "ready" | "launch_required";

export type LaunchBody = { launchToken: string } | { code: string };

/**
 * - opened: the session is ready
 * - refused: the token or code is bad, used or expired (401, 409): the host mints a new one
 * - failed: not refused, not opened (rate limited, unavailable, offline, a bad store). A new
 *   token would meet the same answer, so the host is not asked for one.
 */
export type LaunchResult = "opened" | "refused" | "failed";

/** What an exchange came to, with what an opened one says about where the shopper is. */
type Launched =
	| { result: "opened"; storeId: string; locale: string | undefined }
	| { result: "refused" | "failed" };

/** Waits before retrying a launch that hit a limit or an outage; the token is still unspent. */
const LAUNCH_RETRY_MS = [1_000, 3_000, 8_000];

/**
 * The page's shopper session (ADR-5.3): an opaque token in memory and sessionStorage, sent as
 * `x-shopper-session` on every call to the public API. `lt` / `code` from the URL are
 * exchanged once here, on the client, and dropped from the address bar.
 */
export function useShopperSession({
	page,
	search,
	onLaunched,
	onExpired,
}: {
	page: ShopperPageData;
	search: EmbedSearch;
	/**
	 * The launch is spent: drop `lt`/`code` from the URL and pin the session's store, and
	 * the token's locale hint when it carried one (a reload keeps both). Pass a stable
	 * function: `launch` is rebuilt when it changes.
	 */
	onLaunched: (storeId: string | null, locale?: string) => void;
	/**
	 * A session the page held is gone (expired, revoked, refused launch). Called from requests
	 * in flight, so pass a stable function. A retailer that requires a launch then shows the
	 * launch screen (`launch_required`).
	 */
	onExpired: () => void;
}) {
	const base = `/api/public/${page.orgSlug}`;
	const [status, setStatus] = useState<SessionStatus>("starting");
	const [storeId, setStoreId] = useState(page.storeId);
	const [hostSessionId, setHostSessionId] = useState<string>();
	/** The launch token's `locale` claim: the host's hint, ahead of the browser's language. */
	const [launchLocale, setLaunchLocale] = useState<string>();
	const store = useRef(page.storeId);
	const current = useRef<StoredSession>({
		token: null,
		conversationId: null,
	});
	const save = useCallback(
		() => writeSession(page.orgSlug, store.current, current.current),
		[page.orgSlug],
	);

	/** The session is over: tell the host, and ask for a launch where the retailer requires one. */
	const ended = useCallback(() => {
		onExpired();
		if (page.requireLaunchToken) {
			setStatus("launch_required");
		}
	}, [onExpired, page.requireLaunchToken]);

	/** The session is over: forget it, then `ended`. */
	const drop = useCallback(() => {
		current.current = { token: null, conversationId: null };
		save();
		ended();
	}, [save, ended]);

	// The launch below runs once, from an effect: these read the latest callbacks there.
	const launchedOnLoad = useEffectEvent(onLaunched);
	const endedOnLoad = useEffectEvent(ended);

	/**
	 * fetch for the public API: adds the session, keeps a session or conversation the
	 * server opened (response headers), and forgets a session the server no longer knows.
	 */
	const request = useCallback(
		async (input: string, init: RequestInit = {}) => {
			// A path under this retailer's API, or the chat transport's full URL.
			const url = input.startsWith(base) ? input : `${base}${input}`;
			const sent = current.current.token;
			const headers = new Headers(init.headers);
			if (sent) {
				headers.set(SESSION_HEADER, sent);
			}
			const response = await debugRequest(init.method ?? "GET", url, () =>
				fetch(url, { ...init, headers }),
			);
			const issued = response.headers.get(SESSION_HEADER);
			const conversationId = response.headers.get("x-conversation-id");
			if (issued || conversationId) {
				current.current = {
					token: issued ?? current.current.token,
					conversationId:
						conversationId ?? current.current.conversationId,
				};
				save();
			}
			if (response.status === 401 && sent) {
				debugWarn("session refused by the server: dropped");
				drop();
			}
			return response;
		},
		[base, save, drop],
	);

	/**
	 * Exchanges a launch token or code for an identified session. A 429 or an outage is
	 * retried with backoff: the server refuses those before spending the token.
	 */
	const exchange = useCallback(
		async (body: LaunchBody): Promise<Launched> => {
			let response: Response | null = null;
			for (const wait of [0, ...LAUNCH_RETRY_MS]) {
				if (wait) {
					await new Promise((done) => setTimeout(done, wait));
				}
				response = await debugRequest(
					"POST",
					`${base}/session/launch`,
					() =>
						fetch(`${base}/session/launch`, {
							method: "POST",
							headers: { "content-type": "application/json" },
							// Where the page runs and which SDK opened it, recorded on the session.
							body: JSON.stringify({
								...body,
								embed: search.embed,
								sdk: search.sdk,
							}),
						}),
				).catch(() => null);
				if (
					response &&
					response.status !== 429 &&
					response.status < 500
				) {
					break;
				}
			}
			if (response?.status === 401 || response?.status === 409) {
				debugWarn(`session launch refused: ${response?.status}`);
				return { result: "refused" };
			}
			const token = response?.headers.get(SESSION_HEADER);
			if (!response?.ok || !token) {
				debugError(
					`session launch failed: ${response?.status ?? "no answer"}`,
				);
				return { result: "failed" };
			}
			const data = (await response.json()) as {
				storeId: string;
				conversationId?: string;
				hostSessionId?: string;
				locale?: string;
			};
			store.current = data.storeId;
			setStoreId(data.storeId);
			setHostSessionId(data.hostSessionId);
			setLaunchLocale(data.locale);
			current.current = {
				token,
				conversationId: data.conversationId ?? null,
			};
			save();
			setStatus("ready");
			debugLog(`session launched in store ${data.storeId}`);
			return {
				result: "opened",
				storeId: data.storeId,
				locale: data.locale,
			};
		},
		[base, search.embed, search.sdk, save],
	);

	/**
	 * A launch the host asked for (`session.launch`). The URL's store must name the one the
	 * session is kept under (a relaunch can move the shopper to another store), or a reload
	 * reads the wrong key.
	 */
	const launch = useCallback(
		async (body: LaunchBody): Promise<LaunchResult> => {
			const launched = await exchange(body);
			if (launched.result === "opened") {
				onLaunched(launched.storeId, launched.locale);
			}
			return launched.result;
		},
		[exchange, onLaunched],
	);

	// Once per page load (StrictMode runs effects twice; a launch token is single use).
	const started = useRef(false);
	useEffect(() => {
		if (started.current) {
			return;
		}
		started.current = true;
		const body: LaunchBody | null = search.lt
			? { launchToken: search.lt }
			: search.code
				? { code: search.code }
				: null;
		const restore = () => {
			current.current = readSession(page.orgSlug, store.current);
			setStatus(
				!current.current.token && page.requireLaunchToken
					? "launch_required"
					: "ready",
			);
		};
		if (!body) {
			restore();
			return;
		}
		void exchange(body).then((launched) => {
			if (launched.result === "opened") {
				launchedOnLoad(launched.storeId, launched.locale);
			} else if (launched.result === "refused") {
				// Bad or replayed: off the URL all the same, and the host mints a new
				// one when it hears this.
				launchedOnLoad(store.current);
				restore();
				endedOnLoad();
			} else {
				// Not the token's fault: it stays in the URL, so a reload tries it again.
				restore();
			}
		});
	}, [
		exchange,
		page.orgSlug,
		page.requireLaunchToken,
		search.code,
		search.lt,
	]);

	return useMemo(
		() => ({
			status,
			storeId,
			hostSessionId,
			launchLocale,
			request,
			launch,
			conversationId: () => current.current.conversationId,
			/** Keeps the conversation; starting over passes null and keeps the session. */
			setConversation: (conversationId: string | null) => {
				current.current = { ...current.current, conversationId };
				save();
			},
			/** A session token the call service opened for this page. */
			adopt: (token: string) => {
				current.current = { ...current.current, token };
				save();
			},
			token: () => current.current.token,
			/** The server ended the session outside a request (a call it refused). */
			expire: () => {
				if (current.current.token) {
					drop();
				}
			},
		}),
		[
			status,
			storeId,
			hostSessionId,
			launchLocale,
			request,
			launch,
			save,
			drop,
		],
	);
}

export type ShopperSession = ReturnType<typeof useShopperSession>;
